export type PostStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed';

export type CampaignStatus =
  | 'draft'
  | 'pending_review'
  | 'active'
  | 'paused'
  | 'completed'
  | 'error';

export type ContentTheme =
  | 'product-showcase'
  | 'behind-the-scenes'
  | 'user-engagement'
  | 'seasonal'
  | 'brand-story'
  | 'promotion'
  | 'educational'
  | 'community';

export interface Engagement {
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  reach: number;
  impressions: number;
}

export interface InstagramPost {
  _id: string;
  caption: string;
  hashtags: string[];
  mediaType: string;
  mediaUrls: string[];
  brandAsset?: string;
  status: PostStatus;
  approvalNote?: string;
  scheduledFor?: string;
  publishedAt?: string;
  igPermalink?: string;
  generatedBy: 'ai' | 'manual';
  contentTheme?: ContentTheme;
  engagement: Engagement;
  errorMessage?: string;
  createdAt: string;
}

export interface CampaignPerformance {
  impressions: number;
  reach: number;
  clicks: number;
  ctr: number;
  cpc: number;
  conversions: number;
  costPerConversion: number;
  frequency: number;
}

export interface InstagramCampaign {
  _id: string;
  name: string;
  objective: string;
  status: CampaignStatus;
  budget: { daily?: number; lifetime?: number; currency: string };
  totalBudget: number;
  spent: number;
  startDate?: string;
  endDate?: string;
  targeting: {
    ageMin: number;
    ageMax: number;
    genders: string[];
    interests: string[];
    locations: string[];
  };
  adCreative: {
    headline?: string;
    body?: string;
    callToAction: string;
    mediaUrl?: string;
    linkUrl?: string;
  };
  metaCampaignId?: string;
  performance: CampaignPerformance;
  optimizationGoal: string;
  errorMessage?: string;
  lastSyncedAt?: string;
  createdAt: string;
}

export interface CalendarEntry {
  _id: string;
  title: string;
  description?: string;
  scheduledDate: string;
  dayOfWeek?: string;
  timeSlot?: string;
  contentTheme?: ContentTheme;
  suggestedCaption?: string;
  suggestedHashtags: string[];
  suggestedMedia?: string;
  status: 'suggested' | 'accepted' | 'content_created' | 'skipped';
  postId?: string;
}

export interface BrandAsset {
  file: string;
  description: string;
  url: string;
  // Set on AI-generated media so the composer knows to publish it as a Reel
  // and to render a <video> rather than an <img>.
  kind?: 'image' | 'video';
  // Whether this came out of a model or off an admin's disk. Only used for
  // labelling — both are posted the same way.
  origin?: 'generated' | 'uploaded';
}

export interface TrendTheme {
  theme: string;
  evidence: string;
  opportunity: string;
}

export interface TrendRecommendation {
  title: string;
  contentTheme: ContentTheme;
  rationale: string;
  suggestedCaption: string;
  suggestedHashtags: string[];
  // Written alongside the caption so copy and visual come from one idea, and
  // so the composer is never reached without something to generate from.
  mediaType: 'image' | 'video';
  mediaPrompt: string;
}

export interface TrendStrategy {
  summary: string;
  themes: TrendTheme[];
  recommendations: TrendRecommendation[];
}

export interface MediaPromptSuggestion {
  label: string;
  mediaType: 'image' | 'video';
  prompt: string;
  rationale: string;
}

export interface MediaJob {
  _id: string;
  kind: 'image' | 'video';
  status: 'processing' | 'completed' | 'failed';
  prompt: string;
  aspectRatio: string;
  model?: string;
  origin?: 'generated' | 'uploaded';
  originalFilename?: string;
  mediaUrl?: string;
  storageKey?: string;
  contentType?: string;
  bytes?: number;
  errorMessage?: string;
  completedAt?: string;
  createdAt: string;
}

export interface BudgetSummary {
  monthlyCap: number;
  allocated: number;
  spent: number;
  remaining: number;
  activeCampaigns: number;
}

export interface ConnectionStatus {
  connectionStatus: 'disconnected' | 'connected' | 'token_expired' | 'error';
  configured: boolean;
  contentGenerationEnabled: boolean;
  facebookPageName?: string;
  instagramUsername?: string;
  adAccountId?: string;
  permissions?: string[];
  tokenExpiresAt?: string;
  connectedAt?: string;
  lastError?: string;
  tokenValid?: boolean;
}

export interface AnalyticsOverview {
  account?: {
    username: string;
    followers_count: number;
    follows_count: number;
    media_count: number;
  } | null;
  pendingApproval: number;
  publishedThisMonth: number;
  scheduled: number;
  engagementRate: number;
  totalLikes: number;
  totalComments: number;
  totalReach: number;
  totalImpressions: number;
  budget: BudgetSummary;
}

export const CONTENT_THEMES: { value: ContentTheme; label: string }[] = [
  { value: 'product-showcase', label: 'Product Showcase' },
  { value: 'behind-the-scenes', label: 'Behind the Scenes' },
  { value: 'user-engagement', label: 'User Engagement' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'brand-story', label: 'Brand Story' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'educational', label: 'Educational' },
  { value: 'community', label: 'Community' },
];

export const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  publishing: 'bg-blue-100 text-blue-800',
  published: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-700',
  error: 'bg-red-100 text-red-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
};

export function formatStatus(status: string): string {
  return status.replace(/_/g, ' ').toUpperCase();
}
